/* Sicherheitsbereich der Einstellungen.

   Was hier steht, ist die Bedienoberfläche zu den Endpunkten aus
   auth/routes.js. Ohne sie wären Geräteliste, Widerruf, Verknüpfung und
   Adresswechsel zwar gebaut, aber für niemanden erreichbar – und ein
   Sicherheitsmerkmal, das man nicht bedienen kann, ist keines.

   Die Ereignisliste ist bewusst dabei: „Wann hat sich zuletzt jemand
   angemeldet, und von wo?" ist die Frage, die man sich stellt, wenn etwas
   nicht stimmt – und die man ohne Protokoll nicht beantworten kann. */

import { $, esc } from '../core/text.js';
import { state } from '../core/state.js';
import {
  cloudEvents, cloudLinkProvider, cloudLogoutAll, cloudMe, cloudNonce,
  cloudRequestEmailChange, cloudRevokeSession, cloudSessions, cloudSetPassword,
  cloudUnlinkProvider, cloudAuthConfig,
} from '../auth/account.js';
import { mitLadezustand } from '../ui/busy.js';
import { fehlerBox, ladend, skelSicherheit, wireErneut, zeigeInhalt, zeigeSkeleton } from '../ui/skeleton.js';
import { toast } from '../ui/toast.js';

let laeuft = false;

function melde(text, art) {
  const el = $('#sec-status');
  if (!el) return;
  el.textContent = text || '';
  el.className = 'sec-status' + (art ? ' ' + art : '');
  if (text) setTimeout(() => { if (el.textContent === text) el.textContent = ''; }, 6000);
}

/* Wie in auth/screen.js: Beschriftung bleibt, ein Kreisel kommt davor, die
   Breite ist eingefroren. Das Austauschen des Textes gegen „Einen Moment …"
   ließ jede Zeile im Sicherheitsbereich seitlich springen – bei „Abmelden" in
   der Geräteliste war der Sprung so groß wie der Knopf selbst. */
async function schuetzen(btn, fn) {
  if (laeuft) return;
  laeuft = true;
  try { await mitLadezustand(btn, fn); }
  catch (err) { melde(err.message || 'Das hat nicht geklappt.', 'err'); }
  finally { laeuft = false; }
}

/* Aus dem User-Agent das Wesentliche herausziehen. Die vollständige Zeichenkette
   sagt niemandem etwas; „Chrome auf Windows" beantwortet die eigentliche Frage:
   war ich das? */
function geraet(ua) {
  if (!ua) return 'Unbekanntes Gerät';
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari'
      : /Firefox\//.test(ua) ? 'Firefox' : 'Browser';
  const system = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'macOS'
      : /Linux/.test(ua) ? 'Linux' : '';
  return system ? `${browser} auf ${system}` : browser;
}

const wann = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
    + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const EREIGNIS = {
  login_ok: 'Angemeldet', login_fail: 'Fehlgeschlagene Anmeldung', register: 'Konto erstellt',
  logout: 'Abgemeldet', logout_all: 'Überall abgemeldet', session_revoked: 'Gerät abgemeldet',
  pw_changed: 'Passwort geändert', pw_reset_requested: 'Passwort-Reset angefordert',
  pw_reset_used: 'Passwort zurückgesetzt', email_verified: 'E-Mail bestätigt',
  email_change_requested: 'Adresswechsel angefordert', email_changed: 'E-Mail geändert',
  oauth_login: 'Anmeldung über Anbieter', oauth_linked: 'Anbieter verknüpft',
  oauth_unlinked: 'Verknüpfung gelöst', oauth_takeover: 'Konto an Anbieter übergeben',
};

/* ── Geräte ── */
async function renderSessions() {
  const box = $('#sec-sessions');
  if (!box) return;
  zeigeSkeleton(box, skelSicherheit(2));
  try {
    const { sessions } = await cloudSessions();
    ladend(box, false);
    box.innerHTML = sessions.map((s) => `
      <div class="sec-row${s.current ? ' current' : ''}">
        <div>
          <b>${esc(geraet(s.ua))}</b>${s.current ? ' <span class="sec-tag">dieses Gerät</span>' : ''}
          <div class="sec-sub">zuletzt aktiv ${esc(wann(s.lastSeen))} · angemeldet seit ${esc(wann(s.createdAt))}</div>
        </div>
        ${s.current ? '' : `<button class="btn-ghost outline small" data-revoke="${esc(s.ref)}">Abmelden</button>`}
      </div>`).join('') || '<p class="page-sub">Keine weiteren Geräte.</p>';

    box.querySelectorAll('[data-revoke]').forEach((b) => {
      b.onclick = () => schuetzen(b, async () => {
        await cloudRevokeSession(b.dataset.revoke);
        melde('Gerät abgemeldet.', 'ok');
        await renderSessions();
      });
    });
  } catch (err) {
    /* Gerade hier ist ein Fehler wichtig: „keine weiteren Geräte" und
       „konnte nicht nachsehen" sind sicherheitsrelevant verschieden. */
    zeigeInhalt(box, fehlerBox(err.message || 'Die Geräteliste konnte nicht geladen werden.'));
    wireErneut(box, () => renderSessions());
  }
}

/* ── Anbieter ── */
async function renderProviders() {
  const box = $('#sec-providers');
  if (!box) return;
  zeigeSkeleton(box, skelSicherheit(1));
  const cfg = await cloudAuthConfig();
  ladend(box, false);
  const verbunden = state.me?.providers || [];
  const zeilen = [];

  for (const [key, name, an] of [['google', 'Google', cfg.google], ['apple', 'Apple', cfg.apple]]) {
    if (!an) continue;
    const ist = verbunden.some((p) => p.provider === key);
    zeilen.push(`
      <div class="sec-row">
        <div><b>${name}</b><div class="sec-sub">${ist ? 'verknüpft – du kannst dich damit anmelden' : 'nicht verknüpft'}</div></div>
        <button class="btn-ghost outline small" data-prov="${key}" data-an="${ist ? '0' : '1'}">${ist ? 'Trennen' : 'Verknüpfen'}</button>
      </div>`);
  }
  // Überschrift nennt genau die Anbieter, die es hier auch gibt.
  const namen = [cfg.google && 'Google', cfg.apple && 'Apple'].filter(Boolean);
  const kopf = $('#sec-prov-head');
  if (kopf) kopf.textContent = namen.length ? 'Anmeldung über ' + namen.join(' & ') : 'Anmeldung über andere Anbieter';

  box.innerHTML = zeilen.join('') || '<p class="page-sub">Auf diesem Server ist keine Anmeldung über einen externen Anbieter eingerichtet.</p>';

  box.querySelectorAll('[data-prov]').forEach((b) => {
    b.onclick = () => schuetzen(b, async () => {
      const key = b.dataset.prov;
      if (b.dataset.an === '0') {
        /* Trennen lehnt der Server ab, wenn danach kein Anmeldeweg übrig
           bliebe – die Meldung dazu kommt von dort und ist präziser als
           alles, was wir hier raten könnten. */
        const adresse = verbunden.find((p) => p.provider === key)?.email;
        await cloudUnlinkProvider(key);
        /* Zusätzlich Googles Zustimmung zurückziehen. „Trennen" heißt: diese
           App und dieses Google-Konto haben nichts mehr miteinander zu tun –
           dann soll auch Google es nicht weiter als verbunden führen und den
           personalisierten Knopf („Als … fortfahren") dafür anbieten.
           Beim bloßen Abmelden wäre das zu viel; hier ist es genau richtig. */
        if (key === 'google' && adresse) zustimmungZurueckziehen(adresse);
        melde('Verknüpfung gelöst.', 'ok');
      } else if (key === 'google') {
        await verknuepfeGoogle(cfg.googleClientId);
      } else {
        await verknuepfeApple(cfg.appleClientId, cfg.appUrl || location.origin);
      }
      state.me = await cloudMe();
      await renderProviders();
    });
  });
}

function ladeSkript(src) {
  return new Promise((ok, nein) => {
    if (document.querySelector(`script[src="${src}"]`)) return ok();
    const s = document.createElement('script');
    s.src = src; s.async = true; s.defer = true;
    s.onload = ok;
    s.onerror = () => nein(new Error('Skript nicht erreichbar: ' + src));
    document.head.appendChild(s);
  });
}

/* Verknüpfen läuft über denselben geprüften Weg wie die Anmeldung: signiertes
   ID-Token samt Einmal-Kennung. Der Unterschied ist nur, dass der Server es
   einer bestehenden Sitzung zuordnet statt eine neue zu eröffnen. */
async function verknuepfeGoogle(clientId) {
  await ladeSkript('https://accounts.google.com/gsi/client');
  const g = window.google;
  if (!g?.accounts?.id) throw new Error('Google-Bibliothek nicht verfügbar');

  /* Googles eigener Knopf statt One Tap: `prompt()` wird von Browsern und
     Einstellungen regelmäßig unterdrückt und meldet das nur indirekt – dann
     passiert scheinbar gar nichts. Der gerenderte Knopf funktioniert
     verlässlich, und Google verlangt ihn ohnehin. */
  const host = $('#sec-google-host');
  host.classList.remove('hidden');
  host.innerHTML = '';

  const credential = await new Promise((ok, nein) => {
    cloudNonce().then((nonce) => {
      g.accounts.id.initialize({
        client_id: clientId, ux_mode: 'popup', nonce,
        callback: (r) => (r && r.credential ? ok(r.credential) : nein(new Error('Google hat kein Token geliefert.'))),
      });
      g.accounts.id.renderButton(host, {
        type: 'standard', theme: 'outline', size: 'large',
        text: 'continue_with', shape: 'pill', locale: 'de',
      });
      melde('Bestätige die Verknüpfung mit dem Google-Knopf unten.', '');
    }).catch(nein);
  });

  host.innerHTML = '';
  host.classList.add('hidden');
  await cloudLinkProvider('google', { credential });
  melde('Google verknüpft.', 'ok');
}

async function verknuepfeApple(clientId, redirectURI) {
  await ladeSkript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/de_DE/appleid.auth.js');
  const A = window.AppleID;
  if (!A?.auth) throw new Error('Apple-Bibliothek nicht verfügbar');
  A.auth.init({ clientId, scope: 'name email', redirectURI, usePopup: true, nonce: await cloudNonce() });
  const res = await A.auth.signIn();
  const id_token = res?.authorization?.id_token;
  if (!id_token) throw new Error('Apple hat kein Token geliefert.');
  await cloudLinkProvider('apple', { id_token });
  melde('Apple verknüpft.', 'ok');
}

/* ── Protokoll ── */
async function renderEvents() {
  const box = $('#sec-events');
  if (!box) return;
  zeigeSkeleton(box, skelSicherheit(3));
  try {
    const { events } = await cloudEvents();
    ladend(box, false);
    box.innerHTML = events.map((e) => `
      <div class="sec-row${e.ok ? '' : ' fail'}">
        <div>
          <b>${esc(EREIGNIS[e.type] || e.type)}</b>${e.ok ? '' : ' <span class="sec-tag warn">fehlgeschlagen</span>'}
          <div class="sec-sub">${esc(wann(e.created_at))}${e.ua ? ' · ' + esc(geraet(e.ua)) : ''}</div>
        </div>
      </div>`).join('') || '<p class="page-sub">Noch keine Ereignisse.</p>';
  } catch (err) {
    zeigeInhalt(box, fehlerBox(err.message || 'Das Protokoll ist gerade nicht abrufbar.'));
    wireErneut(box, () => renderEvents());
  }
}

/* ── Zustand oben: hat das Konto ein Passwort, ist die Adresse bestätigt? ── */
function renderZustand() {
  const hatPw = !!state.me?.hasPassword;
  $('#sec-pw-state').textContent = hatPw
    ? 'Dein Konto ist mit einem Passwort geschützt. Beim Ändern werden alle anderen Geräte abgemeldet.'
    : 'Für dieses Konto ist noch kein Passwort gesetzt – du meldest dich über einen Anbieter an. Ein Passwort ist ein zweiter Weg hinein, falls der Anbieter einmal ausfällt.';
  $('#sec-pw-current-wrap').classList.toggle('hidden', !hatPw);
  $('#sec-mail-current-wrap').classList.toggle('hidden', !hatPw);
  $('#sec-mail-state').textContent = state.me?.email
    ? `Aktuell: ${state.me.email}${state.me.emailVerified ? '' : ' (noch nicht bestätigt)'}`
    : '';
}

let verdrahtet = false;
export async function renderSecurity() {
  const panel = $('#sec-panel');
  if (!panel) return;

  if (!verdrahtet) {
    verdrahtet = true;

    $('#form-sec-pw').onsubmit = (e) => {
      e.preventDefault();
      const btn = e.target.querySelector('button[type=submit]');
      schuetzen(btn, async () => {
        state.me = await cloudSetPassword($('#sec-pw-current').value, $('#sec-pw-new').value);
        $('#sec-pw-current').value = ''; $('#sec-pw-new').value = '';
        melde('Passwort gespeichert. Alle anderen Geräte wurden abgemeldet.', 'ok');
        renderZustand();
        await renderSessions();
      });
    };

    $('#form-sec-mail').onsubmit = (e) => {
      e.preventDefault();
      const btn = e.target.querySelector('button[type=submit]');
      schuetzen(btn, async () => {
        await cloudRequestEmailChange($('#sec-mail-new').value.trim(), $('#sec-mail-current').value);
        $('#sec-mail-new').value = ''; $('#sec-mail-current').value = '';
        melde('Wir haben einen Bestätigungslink an die neue Adresse geschickt. '
            + 'Deine bisherige Adresse bekommt zur Sicherheit eine Benachrichtigung.', 'ok');
      });
    };

    $('#btn-sec-logout-all').onclick = (e) => schuetzen(e.currentTarget, async () => {
      if (!confirm('Alle Geräte abmelden – auch dieses hier?')) return;
      await cloudLogoutAll();
      toast('Überall abgemeldet');
      setTimeout(() => location.reload(), 900);
    });

    $('#btn-sec-reload').onclick = (e) => schuetzen(e.currentTarget, async () => {
      state.me = await cloudMe();
      renderZustand();
      await Promise.all([renderSessions(), renderProviders(), renderEvents()]);
    });
  }

  renderZustand();
  await Promise.all([renderSessions(), renderProviders(), renderEvents()]);
}

/* Googles Zustimmung für diese App zurückziehen.

   Google merkt sich, dass ein Konto dieser App einmal zugestimmt hat, und
   zeichnet daraufhin den personalisierten Knopf. Laut Googles Referenz gibt
   es keine Einstellung, um das abzuschalten – `disableAutoSelect()` betrifft
   ausdrücklich nur die automatische Anmeldung, nicht den Knopf. `revoke()`
   ist der einzige Weg, und er passt inhaltlich genau hierher: beim Trennen
   der Verknüpfung.

   Scheitert es (falsche Adresse, Bibliothek nicht geladen), ist das kein
   Grund, das Trennen als fehlgeschlagen zu melden – bei uns ist die
   Verknüpfung dann bereits gelöst. */
function zustimmungZurueckziehen(email) {
  try {
    window.google?.accounts?.id?.revoke(email, (r) => {
      if (r && r.successful === false) console.warn('Google-Zustimmung nicht zurückgezogen:', r.error);
    });
  } catch (err) {
    console.warn('Google-Zustimmung konnte nicht zurückgezogen werden:', err);
  }
}
